/**
 * Custom domains, on the general account page (tasks 844, 847, 850).
 *
 * Report: `project/reports/platform-custom-domains-account-endpoints.md`.
 *
 * A custom domain is an alias of the account's WHOLE endpoint surface family —
 * invocation, MCP and published interfaces all answer on it — which is why it
 * lives here, on the account, rather than on any one AI Service.
 *
 * The section is built around the fact that attaching a domain is a **two-party
 * job with a wait in the middle**: the platform records the hostname instantly,
 * then nothing happens until the customer edits DNS somewhere else entirely, and
 * then a certificate has to be issued. So the row is the instruction while it
 * waits (the exact CNAME to create, with the target the server told us — never a
 * hardcoded hostname), the status badge is the progress, and the list polls
 * itself until every row has settled.
 *
 * **The plan gate is the server's answer, rendered.** Whether this account may
 * hold a domain, and how many, is resolved live from AMS and arrives beside the
 * list. Nothing about entitlements is decided here — an unresolved plan disables
 * the form exactly as a missing one does, because the platform fails closed on
 * a billed AWS resource.
 */
import { useState } from 'react';
import { CheckIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useOptionalToast } from '@/contexts/ToastContext';
import {
  useCreateCustomDomain,
  useCustomDomains,
  useRemoveCustomDomain,
} from '@/hooks/useCustomDomains';
import DeleteResourceControl from '@/components/shared/DeleteResourceControl';
import type {
  CustomDomain,
  CustomDomainEntitlement,
  CustomDomainStatus,
} from '@/types/customDomain';

/**
 * Status copy and colour. Each state answers "whose move is it?" — amber while
 * the customer still has to do something, blue while the platform is working,
 * green when it is serving, red when it stopped.
 */
const STATUS_DISPLAY: Record<CustomDomainStatus, { label: string; className: string }> = {
  pending_dns: {
    label: 'Awaiting DNS',
    className:
      'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  },
  verifying: {
    label: 'Verifying certificate',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
  active: {
    label: 'Active',
    className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
};

/**
 * Apex domains are refused permanently, so the form says so before the request
 * rather than after it. Label counting, exactly as the server does it — a
 * public-suffix list is the only way to catch `acme.co.uk`, and the rule's real
 * job is to stop the common apex before the customer discovers that a CNAME at
 * a zone apex is not legal DNS.
 */
const HOSTNAME_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

const validateHostname = (hostname: string): string | null => {
  const trimmed = hostname.trim().toLowerCase();
  if (!trimmed) return 'Enter the hostname you want to use';
  if (!HOSTNAME_PATTERN.test(trimmed)) return 'That does not look like a hostname';
  if (trimmed.split('.').length < 3) {
    return 'Use a subdomain such as ai.acme.com — a domain on its own cannot hold the CNAME record this needs';
  }
  return null;
};

/**
 * Turn the API's refusal into the section's words. The server sends a
 * machine-readable code AND a human message; the code decides which of these
 * situations it is, and the message is preferred wherever the server knows
 * something this component does not (the account's actual cap, for instance).
 */
const describeCreateError = (error: unknown): string => {
  const err = error as { status?: number; response?: { code?: string; error?: string } };
  const code = err?.response?.code;
  const serverMessage = err?.response?.error;

  switch (code) {
    case 'APEX_NOT_SUPPORTED':
      return 'Custom domains must be a subdomain, such as ai.acme.com. A domain on its own cannot hold the CNAME record this needs.';
    case 'HOSTNAME_TAKEN':
      return 'That hostname is already registered on the platform. If it is yours, remove it where it is registered first.';
    case 'CUSTOM_DOMAIN_NOT_ENTITLED':
      return 'Your plan does not include custom domains.';
    case 'DOMAIN_LIMIT_REACHED':
      // The server's message carries the account's actual cap.
      return serverMessage
        ? `${serverMessage} Remove one before adding another, or move to a plan that allows more.`
        : 'Your plan does not allow another custom domain.';
    case 'DOMAIN_LIMIT_UNRESOLVED':
      return 'Your plan does not define a custom domain allowance. Contact support and we will sort it out.';
    case 'ENTITLEMENTS_UNRESOLVED':
      return 'We could not check your plan just now, so the domain was not registered. Try again in a moment.';
    case 'INSUFFICIENT_ROLE':
      return 'Only the account owner or an admin can add a custom domain.';
    case 'VALIDATION_ERROR':
      return serverMessage || 'That hostname could not be used.';
    default:
      return serverMessage || 'The domain could not be registered. Try again in a moment.';
  }
};

/** The plan gate's copy, for every reason the server can give. */
const gateMessage = (entitlement: CustomDomainEntitlement): string | null => {
  switch (entitlement.reason) {
    case 'no_active_plan':
      return 'Choose a plan to use custom domains.';
    case 'not_entitled':
      return 'Custom domains are not included in your plan. Move to a plan that includes them to serve your AI Services on your own hostname.';
    case 'limit_unresolved':
      return 'Your plan does not define a custom domain allowance, so we cannot add one safely. Contact support and we will sort it out.';
    case 'limit_reached':
      return `Your plan allows ${entitlement.limit} custom domain${entitlement.limit === 1 ? '' : 's'}. Remove one before adding another, or move to a plan that allows more.`;
    case 'entitlements_unresolved':
      return 'We could not check your plan just now. Your existing domains are unaffected; adding one is unavailable until we can.';
    default:
      return null;
  }
};

/** "1 of 3 used" — shown only when there is a real cap to count against. */
const allowanceLabel = (entitlement: CustomDomainEntitlement): string | null => {
  if (!entitlement.entitled) return null;
  if (entitlement.unlimited) return `${entitlement.used} in use · no limit on your plan`;
  if (typeof entitlement.limit !== 'number') return null;
  return `${entitlement.used} of ${entitlement.limit} used`;
};

export function CustomDomainsSection() {
  const featureEnabled = useFlag('innovate.fe.custom-domains.manage');
  const { domains, entitlement, isLoading, isError, refetch } = useCustomDomains({
    enabled: featureEnabled,
  });
  const createDomain = useCreateCustomDomain();
  const removeDomain = useRemoveCustomDomain();
  const toast = useOptionalToast();

  const [hostname, setHostname] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  /** Which row's target was just copied, so its button can confirm briefly. */
  const [copiedTarget, setCopiedTarget] = useState<string | null>(null);

  if (!featureEnabled) return null;

  const copyTarget = async (domain: CustomDomain) => {
    try {
      await navigator.clipboard.writeText(domain.target);
      setCopiedTarget(domain.id);
      toast?.info('CNAME target copied', domain.target);
      setTimeout(() => setCopiedTarget(null), 1500);
    } catch {
      toast?.error('Could not copy', 'Copy the CNAME target manually.');
    }
  };

  const handleCreate = () => {
    const problem = validateHostname(hostname);
    setFormError(problem);
    if (problem) return;

    createDomain.mutate(hostname.trim().toLowerCase(), {
      onSuccess: () => {
        setHostname('');
        setFormError(null);
      },
      // The server's refusal is the message — it is the only party that knows
      // the account's plan, its cap, and what other accounts already hold.
      onError: (error) => setFormError(describeCreateError(error)),
    });
  };

  const allowance = allowanceLabel(entitlement);
  const gate = gateMessage(entitlement);

  return (
    <section
      data-testid="custom-domains-section"
      className="mt-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Custom domains</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Serve your AI Services on a hostname you own — <code>https://ai.acme.com/your-service</code>{' '}
            instead of a platform URL. One domain covers every AI Service in this account, including
            MCP access and published interfaces.
          </p>
        </div>
        {allowance && (
          <span
            data-testid="custom-domains-allowance"
            className="flex-shrink-0 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200"
          >
            {allowance}
          </span>
        )}
      </div>

      {gate && (
        <div
          className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
          data-testid="custom-domains-gate"
        >
          <p className="text-sm text-gray-700 dark:text-gray-300">{gate}</p>
          {(entitlement.reason === 'no_active_plan' ||
            entitlement.reason === 'not_entitled' ||
            entitlement.reason === 'limit_reached') && (
            <a
              href="/settings/subscription"
              data-testid="custom-domains-upgrade-link"
              className="mt-2 inline-block text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              View plans
            </a>
          )}
        </div>
      )}

      {isLoading && (
        <p
          className="mt-4 text-sm text-gray-500 dark:text-gray-400"
          data-testid="custom-domains-loading"
        >
          Loading your custom domains…
        </p>
      )}

      {isError && !isLoading && (
        <div
          className="mt-4 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20"
          data-testid="custom-domains-error"
        >
          <p className="text-sm text-red-700 dark:text-red-300">
            We could not load your custom domains. Any domain already serving traffic is unaffected.
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            data-testid="custom-domains-retry"
            className="mt-2 text-sm font-medium text-red-700 hover:underline dark:text-red-300"
          >
            Try again
          </button>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="mt-4" data-testid="custom-domains-loaded">
          {domains.length === 0 ? (
            <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="custom-domains-empty">
              No custom domains yet. Add a subdomain you own and we will tell you the one DNS record
              to create.
            </p>
          ) : (
            <ul
              className="divide-y divide-gray-200 dark:divide-gray-700"
              data-testid="custom-domains-list"
            >
              {domains.map((domain) => (
                <li key={domain.id} data-testid="custom-domain-row" className="py-3 first:pt-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      data-testid="custom-domain-hostname"
                      className="min-w-0 flex-1 truncate font-mono text-sm text-gray-900 dark:text-white"
                    >
                      {domain.hostname}
                    </span>

                    <span
                      data-testid="custom-domain-status"
                      data-status={domain.status}
                      className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_DISPLAY[domain.status].className}`}
                    >
                      {STATUS_DISPLAY[domain.status].label}
                    </span>

                    {/* The one delete affordance (task 864), on the shared
                        dialog rather than the second hand-rolled modal this
                        section used to carry. `restorable={false}` is load
                        bearing: a custom domain is an AMS registration with no
                        innovate deletion-ledger row behind it, so the Archive
                        cannot bring it back and the dialog must not say it can. */}
                    <DeleteResourceControl
                      flag="innovate.fe.custom-domains.manage"
                      entityLabel="custom domain"
                      name={domain.hostname}
                      id={domain.hostname}
                      restorable={false}
                      testId="custom-domain-remove"
                      body={
                        <p>
                          Traffic to this domain will stop immediately. Your AI Services stay
                          reachable on their platform URLs, and you can register the hostname again
                          later.
                        </p>
                      }
                      onDelete={() => removeDomain.mutateAsync(domain.hostname)}
                    />
                  </div>

                  {/* THE RECORD, PERMANENTLY (task 856). It used to disappear
                      once the domain went active; it must not. The CNAME is
                      both the routing path and the certificate-validation path,
                      so it stays in the customer's DNS for as long as the domain
                      exists — and a User auditing their DNS or moving providers
                      needs to read the value back at any time.

                      What changes with status is only the FRAMING: an
                      instruction while the record is the one thing standing
                      between the customer and a working hostname, a quiet
                      reference once it is serving. The target is always the
                      server's — every record carries it so no surface guesses. */}
                  <p
                    data-testid="custom-domain-cname"
                    data-status={domain.status}
                    className={`mt-1 flex flex-wrap items-center gap-x-2 text-xs ${
                      domain.status === 'active'
                        ? 'text-gray-400 dark:text-gray-500'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <span>
                      {domain.status === 'active' ? (
                        <>
                          DNS record: <code className="font-mono">{domain.hostname}</code> CNAME{' '}
                          <code className="font-mono">{domain.target}</code> — leave it in place.
                        </>
                      ) : (
                        <>
                          Point <code className="font-mono">{domain.hostname}</code> to{' '}
                          <code className="font-mono">{domain.target}</code> with a CNAME record.
                        </>
                      )}
                    </span>
                    {/* The same quiet icon-button copy affordance the Account ID
                        carries on this page. It copies the TARGET — the value
                        that is typed into DNS; the hostname is the customer's
                        own and they already have it. */}
                    <button
                      type="button"
                      onClick={() => void copyTarget(domain)}
                      data-testid="custom-domain-cname-copy"
                      aria-label={`Copy CNAME target for ${domain.hostname}`}
                      title="Copy CNAME target"
                      className="rounded p-0.5 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:text-gray-200"
                    >
                      {copiedTarget === domain.id ? (
                        <CheckIcon className="h-3.5 w-3.5 text-green-500" aria-hidden="true" />
                      ) : (
                        <ClipboardIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      )}
                    </button>
                  </p>

                  {domain.status === 'failed' && domain.failureReason && (
                    <p
                      data-testid="custom-domain-failure"
                      className="mt-1 text-xs text-red-600 dark:text-red-400"
                    >
                      {domain.failureReason}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex flex-wrap items-start gap-2">
            <div className="min-w-0 flex-1">
              <label
                htmlFor="custom-domain-new-hostname"
                className="block text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Hostname
              </label>
              <input
                id="custom-domain-new-hostname"
                value={hostname}
                onChange={(event) => {
                  setHostname(event.target.value);
                  if (formError) setFormError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && entitlement.canAdd) handleCreate();
                }}
                disabled={!entitlement.canAdd}
                placeholder="ai.acme.com"
                aria-label="New custom domain hostname"
                data-testid="custom-domain-new-hostname"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 font-mono text-sm disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                A subdomain you own, such as <code>ai.acme.com</code>. You will create one CNAME
                record to finish.
              </p>
              {formError && (
                <p
                  className="mt-1 text-sm text-red-600 dark:text-red-400"
                  data-testid="custom-domain-form-error"
                >
                  {formError}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handleCreate}
              disabled={!entitlement.canAdd || createDomain.isPending}
              data-testid="custom-domain-create"
              className="mt-5 flex-shrink-0 rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Add domain
            </button>
          </div>
        </div>
      )}

    </section>
  );
}

export default CustomDomainsSection;
